import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchCollection, fetchCollectionV2 } from '../lib/cms';
import Lightbox from '../components/Lightbox';

export default function ServiceDetail() {
  const { slug: urlSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Resolve slug from pathname if useParams().slug is undefined (e.g., for /services/site-office)
  let slug = urlSlug || location.pathname.split('/').pop();
  if (slug === 'site-office') {
    slug = 'site-offices';
  }
  if (slug === 'vaskeri-laundry') {
    slug = 'laundry';
  }

  useEffect(() => {
    async function loadServiceData() {
      setLoading(true);
      try {
        // Fetch all services to find the matching one by slug
        const servicesData = await fetchCollection('services');
        const foundService = servicesData.find(s => s.slug.en === slug);
        
        if (!foundService) {
          // Redirect to 404 if service not found
          navigate('/404');
          return;
        }

        setService(foundService);

        // Fetch gallery items for this service (supporting both singular and plural slugs)
        const galleryData = await fetchCollection('gallery');
        const serviceGallery = galleryData.filter(item => 
          item.service_slug === slug || 
          (slug === 'site-offices' && item.service_slug === 'site-office')
        );
        setGallery(serviceGallery);

        // Fetch references for this service (supporting both singular and plural slugs)
        const referencesData = await fetchCollection('references');
        const serviceReferences = referencesData.filter(ref => 
          ref.service_slug === slug || 
          (slug === 'site-offices' && ref.service_slug === 'site-office')
        );
        setReferences(serviceReferences);

      } catch (error) {
        console.error('Error loading service details:', error);
      } finally {
        setLoading(false);
      }
    }
    loadServiceData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner-third animate-spin text-4xl text-accent" />
          <p className="text-slate-500 font-medium">Loading solution details...</p>
        </div>
      </div>
    );
  }

  if (!service) return null;

  const galleryImages = gallery.map(item => item.image[0]);

  return (
    <div className="pt-[73px] bg-white min-h-screen">
      {/* Hero Header */}
      <section className="bg-primary text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-15">
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${service.image[0]}`}
            alt={service.title}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-3xl space-y-4">
            <Link to="/services" className="inline-flex items-center gap-2 text-xs font-semibold text-accent hover:text-accent-light transition-colors duration-200 uppercase tracking-widest">
              <i className="fa-regular fa-arrow-left" />
              <span>Back to Solutions</span>
            </Link>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-white">
              {service.title}
            </h1>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              {service.summary}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column: Rich Text Content */}
          <div className="lg:col-span-2 space-y-12">
            <div 
              className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-bold prose-headings:text-primary prose-p:leading-relaxed prose-p:text-slate-600 prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-p:mb-6"
              dangerouslySetInnerHTML={{ __html: service.content }}
            />

            {/* Service Specific Video Showcase */}
            {slug === 'hotel-management' && (
              <div className="pt-8 border-t border-slate-100 space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-primary font-display flex items-center gap-3">
                  <i className="fa-regular fa-circle-play text-accent" />
                  <span>Video Presentation</span>
                </h2>
                <div className="relative rounded-xl overflow-hidden shadow-md aspect-video bg-slate-900 border border-slate-100">
                  <video
                    src="https://assets.auxodev.dk/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/s-site-hotel.mp4"
                    controls
                    playsInline
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {slug === 'personnel-transfer-baskets' && (
              <div className="pt-8 border-t border-slate-100 space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-primary font-display flex items-center gap-3">
                  <i className="fa-regular fa-circle-play text-accent" />
                  <span>Video Presentation</span>
                </h2>
                <div className="relative rounded-xl overflow-hidden shadow-md aspect-video bg-slate-900 border border-slate-100">
                  <video
                    src="https://assets.auxodev.dk/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/safe-lift-equipment-rev2-1-1-1.mp4"
                    controls
                    playsInline
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Service Specific Gallery */}
            {gallery.length > 0 && (
              <div className="pt-8 border-t border-slate-100 space-y-6">
                <h2 className="text-xl sm:text-2xl font-bold text-primary font-display">Gallery</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {gallery.map((item, index) => (
                    <div 
                      key={item.id}
                      className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-slate-100 shadow-md cursor-pointer"
                      onClick={() => setLightboxIndex(index)}
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,height=600,fit=cover,quality=85,format=auto/${item.image[0]}`}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="bg-white/95 text-primary h-12 w-12 rounded-full flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                          <i className="fa-regular fa-magnifying-glass-plus text-lg" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-8">
            {/* Key Advantages */}
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100/80 space-y-6">
              <h3 className="font-display text-xs font-bold tracking-widest text-accent uppercase">Key Advantages</h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <i className="fa-regular fa-user-tie-hair text-sm" />
                  </div>
                  <div>
                    <span className="block font-bold text-sm text-primary tracking-wide uppercase">ONE POINT OF CONTACT</span>
                    <span className="text-xs text-slate-500 mt-1 block">Streamlined communication with a dedicated liaison.</span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <i className="fa-regular fa-clock text-sm" />
                  </div>
                  <div>
                    <span className="block font-bold text-sm text-primary tracking-wide uppercase">LESS TRAVEL TIME</span>
                    <span className="text-xs text-slate-500 mt-1 block">Accommodations established close to the work site.</span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <i className="fa-regular fa-shield-check text-sm" />
                  </div>
                  <div>
                    <span className="block font-bold text-sm text-primary tracking-wide uppercase">INCREASED SAFETY</span>
                    <span className="text-xs text-slate-500 mt-1 block">Certified, fully approved facilities and operations.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Contact CTA */}
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100/80 text-center space-y-4">
              <h3 className="font-display text-lg font-bold text-primary">Need more information?</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Contact us today to discuss how we can customize this solution for your project.
              </p>
              <Link
                to="/contact"
                className="block w-full py-3 px-6 rounded-xl bg-accent text-white font-semibold hover:bg-accent-light transition-all-300 shadow-sm"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related References Section (Full Width) */}
      {references.length > 0 && (
        <section className="py-20 bg-white border-t border-slate-100">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
              <span className="text-xs font-bold tracking-widest text-accent uppercase block">Proven Track Record</span>
              <h2 className="font-display text-xl sm:text-2xl md:text-4xl font-extrabold text-primary">Related Case Studies</h2>
              <div className="h-1 w-12 bg-accent rounded-full mx-auto" />
              <p className="text-slate-500 text-sm md:text-base">
                Explore some of our successfully completed projects and custom solutions tailored to this service.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {references.map((ref, idx) => {
                const firstImage = ref.images && ref.images[0] ? ref.images[0] : null;
                const displayImage = firstImage || "https://assets.auxodev.dk/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/ad199c5d-scaled.jpg";
                
                // Format the image URL correctly to avoid double wrapping
                let imageUrl = displayImage;
                if (displayImage.startsWith("http")) {
                  if (displayImage.includes("assets.auxodev.dk") && !displayImage.includes("/cdn-cgi/image/")) {
                    imageUrl = displayImage.replace("https://assets.auxodev.dk/", "https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/");
                  }
                } else {
                  imageUrl = `https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/${displayImage}`;
                }

                return (
                  <Link 
                    key={ref.id}
                    to={`/references/${ref.slug.en}`}
                    className={`group flex flex-col bg-slate-50/50 rounded-2xl border border-slate-100/80 overflow-hidden hover:bg-slate-50 hover:border-accent/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${
                      idx === references.length - 1 && references.length % 2 !== 0 ? 'md:col-span-2 lg:col-span-1' : ''
                    }`}
                  >
                    <div className="aspect-video w-full overflow-hidden relative bg-slate-100">
                      <img 
                        src={imageUrl}
                        alt={ref.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md px-3 py-1 rounded-full border border-slate-100 shadow-sm flex items-center gap-1.5">
                        <i className="fa-solid fa-user-tie text-[10px] text-accent" />
                        <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">{ref.client}</span>
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                      <div className="space-y-2">
                        <h4 className="font-display font-bold text-lg text-primary group-hover:text-accent transition-colors duration-200 line-clamp-1">
                          {ref.title}
                        </h4>
                        <p className="text-slate-500 text-sm line-clamp-2 leading-relaxed">
                          {ref.summary || "Explore how we delivered this tailored solution to meet our client's specific requirements."}
                        </p>
                      </div>
                      
                      <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                        {ref.duration ? (
                          <span className="inline-flex items-center gap-1.5 text-slate-500 font-medium">
                            <i className="fa-regular fa-clock text-accent" />
                            <span>{ref.duration}</span>
                          </span>
                        ) : (
                          <span />
                        )}
                        <span className="inline-flex items-center gap-1 text-accent font-bold group-hover:gap-2 transition-all duration-200">
                          <span>Read Case</span>
                          <i className="fa-regular fa-arrow-right" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox for Gallery */}
      {lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
